import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { loggedInUser } from "src/apollo/cache";
import { CallContext, ModalContext } from "src/context";
import { activity, bell, inbox } from "src/images/NewDesign";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { PhoenixIcon } from "../UI/Phoenix";
import { ActivityCenterModal } from "./ActivityCenterModal";

export type TabType = "activity" | "inbox" | "notifications";

const nullRoutes = ["/onboarding", "/test"];

export const ActivityCenterModule: React.FC = () => {
  const { callState } = useContext(CallContext);
  const { showActivityCenterModal, setShowActivityCenterModal } = useContext(ModalContext);
  const { activityCenter } = useFlags();

  const isRep = useMemo(() => ["AE", "SDR"].includes(loggedInUser()?.role || ""), []);

  const [selectedTab, setSelectedTab] = useState<TabType>("activity");

  const [callMonitorOpened, setCallMonitorOpened] = useState<boolean>(() => {
    const stored = localStorage.getItem("showCallMonitor");
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    const handleStorage = () => {
      const local = localStorage.getItem("showCallMonitor");
      local && setCallMonitorOpened(JSON.parse(local));
    };
    window.addEventListener("callMonitorToggle", handleStorage);
    return () => window.removeEventListener("callMonitorToggle", handleStorage);
  }, []);

  const path = window.location.pathname;
  if (nullRoutes.includes(path) || !activityCenter) return null;

  return (
    <>
      {showActivityCenterModal && (
        <ActivityCenterModal
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          setShowActivityCenterModal={setShowActivityCenterModal}
        />
      )}
      <ModuleContainer
        isRep={isRep}
        onACall={callState}
        callMonitorOpened={callMonitorOpened}
        modalOpened={showActivityCenterModal}
      >
        {isRep && (
          <ModuleButton
            onClick={() => {
              setShowActivityCenterModal(true);
              setSelectedTab("activity");
            }}
          >
            <PhoenixIcon
              svg={activity}
              size={24}
              color={theme.icon.neutral.inverse}
              hoverColor={theme.icon.neutral.inverse}
              pointer
            />
          </ModuleButton>
        )}
        <ModuleButton
          onClick={() => {
            setShowActivityCenterModal(true);
            setSelectedTab("inbox");
          }}
        >
          <PhoenixIcon
            svg={inbox}
            size={24}
            color={theme.icon.neutral.inverse}
            hoverColor={theme.icon.neutral.inverse}
            pointer
          />
        </ModuleButton>
        <ModuleButton
          onClick={() => {
            setShowActivityCenterModal(true);
            setSelectedTab("notifications");
          }}
        >
          <PhoenixIcon
            svg={bell}
            size={24}
            color={theme.icon.neutral.inverse}
            hoverColor={theme.icon.neutral.inverse}
            pointer
          />
        </ModuleButton>
      </ModuleContainer>
    </>
  );
};

const ModuleContainer = styled.div<{
  isRep: boolean;
  callMonitorOpened: boolean;
  modalOpened: boolean;
  onACall: boolean;
}>`
  position: fixed;
  bottom: ${({ isRep, callMonitorOpened }) => (callMonitorOpened ? "252px" : isRep ? "24px" : "64px")};
  right: ${({ onACall }) => (onACall ? "initial" : "24px")};
  left: ${({ onACall }) => (onACall ? "180px" : "initial")};
  z-index: 1000;
  overflow: hidden;

  display: flex;
  width: fit-content;
  height: 40px;

  opacity: ${({ modalOpened }) => (modalOpened ? 0 : 1)};

  border-radius: 40px;
  background: linear-gradient(90deg, #204fc7 0%, #0282f9 100%);
  box-shadow: 0px 15px 4px 0px rgba(0, 0, 0, 0), 0px 10px 4px 0px rgba(0, 0, 0, 0.02),
    0px 5px 3px 0px rgba(0, 0, 0, 0.08), 0px 2px 2px 0px rgba(0, 0, 0, 0.13), 0px 1px 1px 0px rgba(0, 0, 0, 0.15);

  transform: scale(1);
  :hover {
    transform: scale(1.05);
  }
  backface-visibility: hidden;
  transition: transform 0.15s ease-in-out, bottom 0.4s ease-in-out, opacity 0.4s ease-in-out;
`;

const ModuleButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 48px;
  height: 100%;

  border: none;
  background-color: transparent;
  cursor: pointer;

  :hover {
    background-color: ${theme.buttonfill.brand.hover};
  }
`;
